import { useState, useRef } from "react";
import { Upload, FileText, CheckCircle, AlertCircle, Package, Users, Truck, Eye, Download, FileSpreadsheet } from "lucide-react";
import api from "../services/api";

export default function ImportPage() {
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState(null); // "saft" | "csv"
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("upload");
  const [options, setOptions] = useState({
    import_products: true,
    import_customers: true,
    import_suppliers: true,
    merge_duplicates: true,
    update_existing: true
  });
  const fileRef = useRef();

  const detectFileType = (filename) => {
    if (filename.toLowerCase().endsWith('.xml')) return 'saft';
    if (filename.toLowerCase().endsWith('.csv')) return 'csv';
    return null;
  };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (f) {
      const type = detectFileType(f.name);
      setFile(f);
      setFileType(type);
      setPreview(null);
      setResult(null);
      setStep("upload");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) {
      const type = detectFileType(f.name);
      setFile(f);
      setFileType(type);
      setPreview(null);
      setResult(null);
      setStep("upload");
    }
  };

  const handlePreview = async () => {
    if (!file || !fileType) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      let res;
      if (fileType === 'saft') {
        res = await api.post("/saft/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPreview({ type: 'saft', ...res.data });
      } else {
        res = await api.post("/csv/preview-produtos", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPreview({ type: 'csv', ...res.data });
      }
      setStep("preview");
    } catch (e) {
      alert("Erro ao analisar ficheiro: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const handleImport = async () => {
    if (!file || !fileType) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      let res;
      if (fileType === 'saft') {
        const params = new URLSearchParams({
          import_products: options.import_products,
          import_customers: options.import_customers,
          import_suppliers: options.import_suppliers,
          merge_duplicates: options.merge_duplicates
        });
        res = await api.post(`/saft/import?${params}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setResult({ type: 'saft', ...res.data });
      } else {
        res = await api.post(`/csv/import-produtos?update_existing=${options.update_existing}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setResult({ type: 'csv', ...res.data });
      }
      setStep("result");
    } catch (e) {
      alert("Erro na importação: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const reset = () => { setFile(null); setFileType(null); setPreview(null); setResult(null); setStep("upload"); };

  const isSaft = fileType === 'saft';
  const isCsv = fileType === 'csv';

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Importar Dados</h1>
        <p className="text-gray-400 mt-1">Importa dados via ficheiro SAF-T (XML) ou exportação de produtos (CSV) do Vendus</p>
      </div>

      {/* Steps */}
      <div className="flex items-center gap-4 text-sm">
        {["upload","preview","result"].map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${step === s ? "bg-green-500 text-white" : i < ["upload","preview","result"].indexOf(step) ? "bg-green-800 text-green-300" : "bg-gray-700 text-gray-400"}`}>{i+1}</div>
            <span className={step === s ? "text-white font-medium" : "text-gray-500"}>{["Carregar Ficheiro","Pré-visualizar","Resultado"][i]}</span>
            {i < 2 && <div className="w-8 h-px bg-gray-700"/>}
          </div>
        ))}
      </div>

      {/* Upload */}
      {step === "upload" && (
        <div className="space-y-4">
          {/* Tipos suportados */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 flex items-start gap-3">
              <FileText className="w-5 h-5 text-blue-400 mt-0.5 shrink-0"/>
              <div>
                <p className="text-white text-sm font-medium">SAF-T (XML)</p>
                <p className="text-gray-500 text-xs mt-0.5">Produtos, clientes e fornecedores. Exportado do Vendus em Configurações → SAF-T.</p>
              </div>
            </div>
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 flex items-start gap-3">
              <FileSpreadsheet className="w-5 h-5 text-green-400 mt-0.5 shrink-0"/>
              <div>
                <p className="text-white text-sm font-medium">Produtos (CSV)</p>
                <p className="text-gray-500 text-xs mt-0.5">Atualiza PVP, preço de custo, categoria e marca. Exportado do Vendus em Produtos → Exportar.</p>
              </div>
            </div>
          </div>

          {/* Drop zone */}
          <div
            onClick={() => fileRef.current.click()}
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${file ? 'border-green-500 bg-green-900/10' : 'border-gray-600 hover:border-green-500'}`}
          >
            {file ? (
              <div className="space-y-2">
                {isSaft ? <FileText className="w-10 h-10 text-blue-400 mx-auto"/> : <FileSpreadsheet className="w-10 h-10 text-green-400 mx-auto"/>}
                <p className="text-white font-medium">{file.name}</p>
                <p className="text-gray-400 text-sm">{isSaft ? '📋 SAF-T XML — importa produtos, clientes e fornecedores' : '📊 CSV — atualiza produtos com preços e categorias'}</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="w-10 h-10 text-gray-500 mx-auto"/>
                <p className="text-white font-medium">Clica ou arrasta o ficheiro aqui</p>
                <p className="text-gray-500 text-sm">Aceita ficheiros .xml (SAF-T) e .csv (Produtos Vendus)</p>
              </div>
            )}
            <input ref={fileRef} type="file" accept=".xml,.csv" className="hidden" onChange={handleFile}/>
          </div>

          {/* Opções */}
          {file && isSaft && (
            <div className="bg-gray-800 rounded-xl p-4 space-y-3">
              <h3 className="text-white font-medium text-sm">Opções SAF-T</h3>
              {[["import_products","Importar Produtos"],["import_customers","Importar Clientes"],["import_suppliers","Importar Fornecedores"],["merge_duplicates","Atualizar duplicados (por NIF/referência)"]].map(([key, label]) => (
                <label key={key} className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={options[key]} onChange={e => setOptions(o => ({...o, [key]: e.target.checked}))} className="w-4 h-4 accent-green-500"/>
                  <span className="text-gray-300 text-sm">{label}</span>
                </label>
              ))}
            </div>
          )}

          {file && isCsv && (
            <div className="bg-gray-800 rounded-xl p-4 space-y-3">
              <h3 className="text-white font-medium text-sm">Opções CSV</h3>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={options.update_existing} onChange={e => setOptions(o => ({...o, update_existing: e.target.checked}))} className="w-4 h-4 accent-green-500"/>
                <span className="text-gray-300 text-sm">Atualizar produtos existentes (PVP, custo, categoria, marca)</span>
              </label>
            </div>
          )}

          {file && fileType && (
            <button onClick={handlePreview} disabled={loading} className="w-full bg-green-600 hover:bg-green-500 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50">
              <Eye className="w-5 h-5"/>
              {loading ? "A analisar..." : "Pré-visualizar"}
            </button>
          )}

          {file && !fileType && (
            <div className="bg-red-900/30 border border-red-700 rounded-xl p-4 text-red-400 text-sm">
              Formato não reconhecido. Usa um ficheiro .xml (SAF-T) ou .csv (exportação Vendus).
            </div>
          )}
        </div>
      )}

      {/* Preview */}
      {step === "preview" && preview && (
        <div className="space-y-4">
          {/* SAF-T Preview */}
          {preview.type === 'saft' && (
            <>
              {preview.header?.company_name && (
                <div className="bg-gray-800 rounded-xl p-4">
                  <p className="text-white font-medium">{preview.header.company_name}</p>
                  <p className="text-gray-500 text-sm">NIF: {preview.header.tax_registration_number} | Período: {preview.header.start_date} — {preview.header.end_date}</p>
                </div>
              )}
              <div className="grid grid-cols-3 gap-4">
                {[{icon: Package, label: "Produtos", count: preview.counts?.products, color: "text-blue-400"},
                  {icon: Users, label: "Clientes", count: preview.counts?.customers, color: "text-green-400"},
                  {icon: Truck, label: "Fornecedores", count: preview.counts?.suppliers, color: "text-yellow-400"}
                ].map(({icon: Icon, label, count, color}) => (
                  <div key={label} className="bg-gray-800 rounded-xl p-4 text-center">
                    <Icon className={`w-8 h-8 ${color} mx-auto mb-2`}/>
                    <p className="text-2xl font-bold text-white">{count}</p>
                    <p className="text-gray-400 text-sm">{label}</p>
                  </div>
                ))}
              </div>
              {preview.preview?.products?.length > 0 && (
                <div className="bg-gray-800 rounded-xl p-4">
                  <h3 className="text-white font-medium mb-3 text-sm">Primeiros produtos</h3>
                  <div className="space-y-2">
                    {preview.preview.products.map((p, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-gray-300">{p.name}</span>
                        <span className="text-gray-500">PVP: {p.sell_price > 0 ? p.sell_price.toFixed(2)+'€' : 'n/d'} | IVA: {p.vat_rate}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* CSV Preview */}
          {preview.type === 'csv' && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-800 rounded-xl p-4 text-center">
                  <Package className="w-8 h-8 text-green-400 mx-auto mb-2"/>
                  <p className="text-2xl font-bold text-white">{preview.total}</p>
                  <p className="text-gray-400 text-sm">Produtos</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4 text-center">
                  <CheckCircle className="w-8 h-8 text-blue-400 mx-auto mb-2"/>
                  <p className="text-2xl font-bold text-white">{preview.total - (preview.skipped || 0)}</p>
                  <p className="text-gray-400 text-sm">A importar</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4 text-center">
                  <AlertCircle className="w-8 h-8 text-yellow-400 mx-auto mb-2"/>
                  <p className="text-2xl font-bold text-white">{preview.skipped || 0}</p>
                  <p className="text-gray-400 text-sm">Ignorados</p>
                </div>
              </div>
              {preview.preview?.length > 0 && (
                <div className="bg-gray-800 rounded-xl p-4">
                  <h3 className="text-white font-medium mb-3 text-sm">Primeiros produtos</h3>
                  <div className="space-y-2">
                    {preview.preview.map((p, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <div>
                          <span className="text-gray-300">{p.name}</span>
                          {p.brand && <span className="text-gray-500 ml-2">· {p.brand}</span>}
                        </div>
                        <span className="text-gray-500">PVP: {p.sell_price?.toFixed(2)}€ | Custo: {p.cost_price?.toFixed(2)}€ | {p.category}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {preview.errors?.length > 0 && (
            <div className="bg-red-900/30 border border-red-700 rounded-xl p-4">
              <h3 className="text-red-400 font-medium mb-2 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4"/> Avisos ({preview.errors.length})</h3>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {preview.errors.map((e, i) => <p key={i} className="text-red-300 text-xs">{e}</p>)}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={() => setStep("upload")} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-xl font-medium text-sm">Voltar</button>
            <button onClick={handleImport} disabled={loading} className="flex-1 bg-green-600 hover:bg-green-500 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50 text-sm">
              <Download className="w-4 h-4"/>
              {loading ? "A importar..." : "Importar Agora"}
            </button>
          </div>
        </div>
      )}

      {/* Result */}
      {step === "result" && result && (
        <div className="space-y-4">
          <div className="bg-green-900/30 border border-green-700 rounded-xl p-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3"/>
            <h2 className="text-xl font-bold text-white">Importação Concluída!</h2>
          </div>

          {result.type === 'saft' && (
            <div className="grid grid-cols-3 gap-4">
              {[
                {label: "Produtos", imported: result.products_imported, updated: result.products_updated, skipped: result.products_skipped},
                {label: "Clientes", imported: result.customers_imported, updated: result.customers_updated, skipped: result.customers_skipped},
                {label: "Fornecedores", imported: result.suppliers_imported, updated: result.suppliers_updated, skipped: result.suppliers_skipped},
              ].map(({label, imported, updated, skipped}) => (
                <div key={label} className="bg-gray-800 rounded-xl p-4">
                  <h3 className="text-white font-medium mb-3 text-sm">{label}</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-gray-400">Novos</span><span className="text-green-400 font-bold">{imported}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Atualizados</span><span className="text-blue-400 font-bold">{updated}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Ignorados</span><span className="text-gray-500 font-bold">{skipped}</span></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {result.type === 'csv' && (
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gray-800 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-green-400">{result.imported}</p>
                <p className="text-gray-400 text-sm">Novos produtos</p>
              </div>
              <div className="bg-gray-800 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-blue-400">{result.updated}</p>
                <p className="text-gray-400 text-sm">Atualizados</p>
              </div>
              <div className="bg-gray-800 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-gray-500">{result.skipped}</p>
                <p className="text-gray-400 text-sm">Ignorados</p>
              </div>
            </div>
          )}

          {result.errors?.length > 0 && (
            <div className="bg-red-900/30 border border-red-700 rounded-xl p-4">
              <h3 className="text-red-400 font-medium mb-2 text-sm">Erros ({result.errors.length})</h3>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {result.errors.map((e, i) => <p key={i} className="text-red-300 text-xs">{e}</p>)}
              </div>
            </div>
          )}

          <button onClick={reset} className="w-full bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-xl font-medium text-sm">
            Nova Importação
          </button>
        </div>
      )}
    </div>
  );
}
